"use client";

import dynamic from "next/dynamic";
import get from "lodash.get";
import PropTypes from "prop-types";
import { constants } from "../../../constants/pageConstants/pdpConstants";

const ImagePersonalization = dynamic(() => import("../../../components/organisms/imagePersonalization"));
const TextPersonalization = dynamic(
  () => import("../../../components/molecules/pdpPersonalizations/textPersonalization"),
);
const MultiImageWithTextPersonalization = dynamic(
  () => import("../../../components/molecules/pdpPersonalizations/multiImageWithTextPersonalization"),
);
const MultiTextPersonalization = dynamic(
  () => import("../../../components/molecules/pdpPersonalizations/multiTextPersonalization"),
);

/**
 * Renders a section with personalized options for a product.
 *
 * This component displays personalized options based on the `currentItem` prop. It processes the
 * personalized options and renders appropriate UI elements, such as text fields, based on the content type.
 *
 * @param {Object} currentItem - An object representing the current item with personalized options.
 * @param {bool} isMobile - Mobile check.
 * @returns {JSX.Element} The rendered section with personalized options.
 */
const PersonalizedOptionSection = ({ currentItem, isMobile }) => {
  const { personalizedOptions, personalizedProduct } = get(currentItem, "personalizedInfo");
  let textTypeCount = 0;

  /**
   * Function to handle the rendering of personalized options for a product.
   *   * @returns {Array} An components or other UI components based on the content type.
   */
  const handlePersonalizedOptions = () => {
    if (!personalizedProduct) return null;

    if (personalizedOptions.length > 2) {
      const multiTextPersonalization = personalizedOptions.every((option) => option?.contentType === "TEXT");
      if (multiTextPersonalization) {
        return (
          <MultiTextPersonalization
            productId={currentItem?.productId}
            isMobile={isMobile}
            personalizedOptions={personalizedOptions}
          />
        );
      } else {
        return (
          <MultiImageWithTextPersonalization
            isMobile={isMobile}
            personalizedOptions={personalizedOptions}
            productId={currentItem?.productId}
          />
        );
      }
    }
    if (personalizedProduct) {
      const personalizations = personalizedOptions.map((option, index) => {
        switch (option.contentType) {
          case constants.IMAGE:
          case constants.AUDIO:
            return (
              <ImagePersonalization
                item={option}
                productId={currentItem?.productId}
                urlIdentifier={currentItem?.urlIdentifier}
                isCustomizedImage={currentItem?.hasPersonalizableUI}
                isMobile={isMobile}
                key={`image-personalization-${index}`}
                currentItem={currentItem}
              />
            );
          case constants.TEXT:
            textTypeCount++;
            if (currentItem?.hasPersonalizableUI) {
              return (
                <ImagePersonalization
                  item={option}
                  productId={currentItem?.productId}
                  urlIdentifier={currentItem?.urlIdentifier}
                  isCustomizedImage={currentItem?.hasPersonalizableUI}
                  isMobile={isMobile}
                  key={`image-personalization-${index}`}
                  currentItem={currentItem}
                />
              );
            }
            return (
              <TextPersonalization
                indexNumber={index}
                textTypeCount={textTypeCount}
                option={option}
                isMobile={isMobile}
                productId={currentItem?.productId}
                key={`text-personalization-${index}`}
                currentItem={currentItem}
              />
            );

          default:
            return null;
        }
      });
      return <>{personalizations}</>;
    }
  };

  return <>{handlePersonalizedOptions()}</>;
};

PersonalizedOptionSection.propTypes = {
  currentItem: PropTypes.objectOf(PropTypes.any).isRequired,
  isMobile: PropTypes.bool.isRequired,
};

export default PersonalizedOptionSection;
